import type {
  InvalidSgmOpportunity,
  MultipleAvailableOpportunity,
  MultipleMinOddsRestrictedOpportunity,
  MultipleOpportunity,
  SgmOpportunity,
  SingleAvailableOpportunity,
  SingleMinOddsRestrictedOpportunity,
  SingleOpportunity,
  SingleUnavailableOpportunity
} from '@swf/generated/types/api/sports';
import { Feature } from '@swf/generated/types/api/sports';

const SGM_FEATURES = [Feature.SGM, Feature.SGM_MULTIPLES];

type Opportunity = SingleOpportunity | MultipleOpportunity;

export const getIsSingleOpportunity = (
  opportunity: Opportunity
): opportunity is SingleOpportunity => opportunity.type === 'SINGLE';

export const getIsAvailableOpportunity = (
  opportunity: Opportunity
): opportunity is SingleAvailableOpportunity | MultipleAvailableOpportunity =>
  opportunity.status === 'AVAILABLE';

export const getIsSingleAvailableOpportunity = (
  opportunity: Opportunity
): opportunity is SingleAvailableOpportunity =>
  getIsSingleOpportunity(opportunity) && getIsAvailableOpportunity(opportunity);

export const getIsSuspendedOpportunity = (
  opportunity: Opportunity
): opportunity is SingleUnavailableOpportunity =>
  getIsSingleOpportunity(opportunity) && opportunity.status === 'SUSPENDED';

export const getIsUnavailableOpportunity = (
  opportunity: Opportunity
): opportunity is SingleUnavailableOpportunity | InvalidSgmOpportunity =>
  opportunity.status === 'UNAVAILABLE';

export const getIsSingleUnavailableOpportunity = (
  opportunity: Opportunity
): opportunity is SingleUnavailableOpportunity =>
  getIsSingleOpportunity(opportunity) && getIsUnavailableOpportunity(opportunity);

export const getIsMultipleOpportunity = (
  opportunity: Opportunity
): opportunity is MultipleOpportunity => opportunity.type !== 'SINGLE';

export const getIsSgmOpportunity = (
  opportunity: Opportunity
): opportunity is SgmOpportunity | InvalidSgmOpportunity =>
  getIsMultipleOpportunity(opportunity) &&
  Boolean(opportunity.features?.some((feature) => SGM_FEATURES.includes(feature)));

export const getIsSgmMultipleOpportunity = (
  opportunity: Opportunity
): opportunity is SgmOpportunity | InvalidSgmOpportunity =>
  getIsMultipleOpportunity(opportunity) &&
  Boolean(opportunity.features?.some((feature) => feature === Feature.SGM_MULTIPLES));

export const getIsSgmInvalidOpportunity = (
  opportunity: Opportunity
): opportunity is InvalidSgmOpportunity =>
  getIsSgmOpportunity(opportunity) && getIsUnavailableOpportunity(opportunity);

export const getIsRestrictedMinOddsOpportunity = (
  opportunity: Opportunity
): opportunity is SingleMinOddsRestrictedOpportunity | MultipleMinOddsRestrictedOpportunity =>
  opportunity.status === 'RESTRICTED' &&
  'restrictedReason' in opportunity &&
  opportunity.restrictedReason === 'BELOW_MIN_ODDS';
